import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { it } from '../../services/api';
import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import { theme } from '../../theme';

function SLABadge({ label, color, hours }) {
  const display = hours < 1
    ? `${Math.round(hours * 60)}m`
    : hours < 24
    ? `${Math.round(hours)}h`
    : `${Math.round(hours / 24)}d`;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.2rem' }}>
      <span style={{ fontSize: '0.7rem', fontWeight: 700, color, background: color + '18', padding: '0.2rem 0.5rem', borderRadius: '0.375rem', whiteSpace: 'nowrap' }}>{label}</span>
      <span style={{ fontSize: '0.7rem', color: theme.textMuted }}>{display} old</span>
    </div>
  );
}

const PRIORITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };

function MyQueue() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [updating, setUpdating] = useState(null); // ticket id being updated
  const [statusDraft, setStatusDraft] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    it.getMyTicketQueue()
      .then((r) => { setTickets(r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleStatusUpdate = async (ticketId, newStatus) => {
    setUpdating(ticketId);
    try {
      await it.updateTicket(ticketId, { status: newStatus });
      load();
    } catch { alert('Failed to update.'); } finally { setUpdating(null); }
  };

  const filtered = tickets
    .filter((t) => (!filterStatus || t.status === filterStatus) && (!filterPriority || t.priority === filterPriority))
    .sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9));

  const sel = { padding: '0.5rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none' };

  const openCount = tickets.filter((t) => t.status === 'open').length;
  const inProgressCount = tickets.filter((t) => t.status === 'in_progress').length;
  const criticalCount = tickets.filter((t) => t.priority === 'critical' || t.priority === 'high').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>My Queue</h1>
        <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>Tickets assigned to you</p>
      </div>

      {/* Summary pills */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        {[
          { label: 'Total Assigned', value: tickets.length, color: theme.primary },
          { label: 'Open', value: openCount, color: theme.warning },
          { label: 'In Progress', value: inProgressCount, color: theme.info },
          { label: 'High / Critical', value: criticalCount, color: theme.danger },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ background: theme.white, border: `1px solid ${theme.border}`, borderRadius: '0.75rem', padding: '0.625rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontSize: '1.25rem', fontWeight: 800, color }}>{value}</span>
            <span style={{ fontSize: '0.8rem', color: theme.textMuted, fontWeight: 500 }}>{label}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <Card padding="0.875rem">
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <select style={sel} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="escalated">Escalated</option>
            <option value="resolved">Resolved</option>
          </select>
          <select style={sel} value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}>
            <option value="">All Priority</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          {(filterStatus || filterPriority) && (
            <Button variant="outlineGray" size="sm" onClick={() => { setFilterStatus(''); setFilterPriority(''); }}>Clear</Button>
          )}
          <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: theme.textMuted }}>{filtered.length} ticket{filtered.length !== 1 ? 's' : ''}</span>
        </div>
      </Card>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: theme.textMuted }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '3rem 0' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>✅</div>
            <p style={{ color: theme.textMuted, margin: 0 }}>No tickets in your queue</p>
          </div>
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filtered.map((t) => (
            <div key={t.id} style={{ background: theme.white, border: `1px solid ${theme.border}`, borderRadius: '0.75rem', padding: '1rem 1.25rem', display: 'flex', gap: '1rem', alignItems: 'flex-start', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              {/* SLA column */}
              <div style={{ flexShrink: 0, width: '64px', textAlign: 'center', paddingTop: '0.125rem' }}>
                <SLABadge label={t.sla_label} color={t.sla_color} hours={t.age_hours} />
              </div>

              {/* Main content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.35rem' }}>
                  <code style={{ fontSize: '0.75rem', color: theme.textMuted, background: theme.pageBg, padding: '0.15rem 0.4rem', borderRadius: '0.25rem', border: `1px solid ${theme.border}` }}>{t.ticket_code}</code>
                  <Badge status={t.priority} />
                  <Badge status={t.status} />
                </div>
                <p style={{ fontWeight: 700, fontSize: '0.9rem', color: theme.text, margin: '0 0 0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</p>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.78rem', color: theme.textMuted }}>
                  <span>Raised by: <strong style={{ color: theme.text }}>{t.raised_by_name || '—'}</strong></span>
                  {t.asset_name && <span>Asset: <strong style={{ color: theme.text }}>{t.asset_name}</strong></span>}
                  <span>Category: <strong style={{ color: theme.text }}>{t.category}</strong></span>
                </div>
              </div>

              {/* Quick status + actions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-end', flexShrink: 0 }}>
                <Button size="sm" variant="outline" onClick={() => navigate(`/it/tickets/${t.id}`)}>View</Button>
                {t.status === 'open' && (
                  <Button size="sm" variant="primary" loading={updating === t.id}
                    onClick={() => handleStatusUpdate(t.id, 'in_progress')}>
                    Start
                  </Button>
                )}
                {t.status === 'in_progress' && (
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <select
                      style={{ padding: '0.3rem 0.5rem', border: `1px solid ${theme.border}`, borderRadius: '0.375rem', fontSize: '0.78rem', color: theme.text, outline: 'none' }}
                      value={statusDraft[t.id] || ''}
                      onChange={(e) => setStatusDraft((s) => ({ ...s, [t.id]: e.target.value }))}
                    >
                      <option value="">Move to…</option>
                      <option value="resolved">Resolved</option>
                      <option value="escalated">Escalated</option>
                    </select>
                    {statusDraft[t.id] && (
                      <Button size="sm" variant="primary" loading={updating === t.id}
                        onClick={() => { handleStatusUpdate(t.id, statusDraft[t.id]); setStatusDraft((s) => { const n = { ...s }; delete n[t.id]; return n; }); }}>
                        OK
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MyQueue;
